/**
 * Admin Promo Settings — control panel for `app_settings.promo:*` docs.
 *
 * Multi-promo: shows a tab strip listing every promo plus a "+ New" tab
 * to create one. Selecting a tab shows that promo's editor, including
 * drag-and-drop image upload zones for the flyer + OG card. Built-in
 * seeded promos (currently `market-research`) cannot be deleted — admin
 * can only toggle them off via `enabled=false`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Megaphone, RefreshCw, Save, ImageIcon, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';
import { PromoImageDropzone } from '@/components/admin/PromoImageDropzone';

// Seeded keys that the backend refuses to delete. Hardcoded here only
// for UI affordance — backend is the source of truth either way.
const SEEDED_KEYS = ['market-research'];

function isFutureDate(end) {
  if (!end) return true;
  try { return new Date(`${end}T23:59:59Z`) >= new Date(); } catch (_) { return false; }
}

function PromoEditor({ promoKey, onChange }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiFetch(`/api/admin/promo/${promoKey}`);
    setLoading(false);
    if (error) { toast.error(error); return; }
    setDoc(data);
  }, [promoKey]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setDoc((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!doc) return;
    setSaving(true);
    const { error } = await apiFetch(`/api/admin/promo/${promoKey}`, {
      method: 'PUT',
      body: {
        enabled: !!doc.enabled,
        start_date: doc.start_date,
        end_date: doc.end_date,
        headline: doc.headline,
        subhead: doc.subhead,
        body: doc.body,
        cta_label: doc.cta_label,
        form_url: doc.form_url,
      },
    });
    setSaving(false);
    if (error) { toast.error(error || 'Save failed'); return; }
    toast.success('Promo saved');
    load();
    onChange?.();
  };

  const regenerate = async () => {
    setRegenerating(true);
    const { error } = await apiFetch(`/api/admin/promo/${promoKey}/regenerate-image`, {
      method: 'POST',
    });
    setRegenerating(false);
    if (error) { toast.error(error || 'Regenerate failed'); return; }
    toast.success('Promo image regenerated. Hard-refresh to see the new asset.');
    load();
  };

  if (loading || !doc) {
    return <div className="text-sm text-muted-foreground py-4">Loading…</div>;
  }

  const liveActive = !!doc.enabled && isFutureDate(doc.end_date);
  const isSeeded = SEEDED_KEYS.includes(promoKey);

  return (
    <div className="space-y-4" data-testid={`promo-editor-${promoKey}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={liveActive
              ? 'border-green-500/40 text-green-700 dark:text-green-400'
              : 'border-muted-foreground/40 text-muted-foreground'}
            data-testid={`promo-live-status-${promoKey}`}
          >
            {liveActive ? 'Live now' : 'Not visible'}
          </Badge>
          {isSeeded && (
            <Badge variant="secondary" className="text-[10px]">Built-in</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id={`promo-enabled-${promoKey}`}
            checked={!!doc.enabled}
            onCheckedChange={(v) => set('enabled', v)}
            data-testid={`promo-enabled-switch-${promoKey}`}
          />
          <Label htmlFor={`promo-enabled-${promoKey}`} className="text-xs">Enabled</Label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor={`promo-start-${promoKey}`}>Start date</Label>
          <Input
            id={`promo-start-${promoKey}`} type="date"
            value={doc.start_date || ''}
            onChange={(e) => set('start_date', e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`promo-end-${promoKey}`}>End date (auto-hides after)</Label>
          <Input
            id={`promo-end-${promoKey}`} type="date"
            value={doc.end_date || ''}
            onChange={(e) => set('end_date', e.target.value)}
            data-testid={`promo-end-date-${promoKey}`}
          />
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label htmlFor={`promo-headline-${promoKey}`}>Headline</Label>
          <Input id={`promo-headline-${promoKey}`} value={doc.headline || ''} onChange={(e) => set('headline', e.target.value)} />
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label htmlFor={`promo-subhead-${promoKey}`}>Subhead</Label>
          <Input id={`promo-subhead-${promoKey}`} value={doc.subhead || ''} onChange={(e) => set('subhead', e.target.value)} />
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label htmlFor={`promo-body-${promoKey}`}>Body</Label>
          <Textarea id={`promo-body-${promoKey}`} rows={3} value={doc.body || ''} onChange={(e) => set('body', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`promo-cta-${promoKey}`}>CTA label</Label>
          <Input id={`promo-cta-${promoKey}`} value={doc.cta_label || ''} onChange={(e) => set('cta_label', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`promo-url-${promoKey}`}>Form URL</Label>
          <Input id={`promo-url-${promoKey}`} type="url" value={doc.form_url || ''} onChange={(e) => set('form_url', e.target.value)} />
        </div>
      </div>

      {/* Drag-and-drop uploads */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
        <PromoImageDropzone
          promoKey={promoKey}
          kind="flyer"
          currentPath={doc.image_path}
          onUploaded={load}
        />
        <PromoImageDropzone
          promoKey={promoKey}
          kind="og"
          currentPath={doc.og_image_path}
          onUploaded={load}
        />
      </div>

      <div className="flex flex-wrap gap-2 pt-2 border-t">
        <Button onClick={save} disabled={saving} data-testid={`promo-save-${promoKey}`}>
          {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save
        </Button>
        {isSeeded && (
          <Button variant="outline" onClick={regenerate} disabled={regenerating} data-testid={`promo-regenerate-${promoKey}`}>
            {regenerating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <ImageIcon className="h-4 w-4 mr-2" />}
            Regenerate from script
          </Button>
        )}
      </div>

      {doc.updated_at && (
        <p className="text-[11px] text-muted-foreground">
          Last edited {new Date(doc.updated_at).toLocaleString()} by {doc.updated_by || 'system'}
        </p>
      )}
    </div>
  );
}

function NewPromoDialog({ open, onOpenChange, onCreated }) {
  const [key, setKey] = useState('');
  const [headline, setHeadline] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    const { data, error } = await apiFetch('/api/admin/promo', {
      method: 'POST',
      body: { key, headline: headline || 'New Promo', enabled: false },
    });
    setSubmitting(false);
    if (error) { toast.error(error || 'Create failed'); return; }
    toast.success(`Promo '${data.key}' created`);
    setKey(''); setHeadline('');
    onOpenChange(false);
    onCreated?.(data.key);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create new promo</DialogTitle>
          <DialogDescription>
            Each promo is keyed by a short URL-safe slug. Use it in `/api/promo/&lt;key&gt;` to fetch the doc on the frontend.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label htmlFor="new-promo-key">Key (lowercase, hyphens)</Label>
            <Input
              id="new-promo-key"
              placeholder="e.g. holiday-2026"
              value={key}
              onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              data-testid="new-promo-key-input"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-promo-headline">Headline</Label>
            <Input
              id="new-promo-headline"
              placeholder="Holiday Special"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              data-testid="new-promo-headline-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || key.length < 2}
            data-testid="new-promo-create-btn"
          >
            {submitting ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PromoSettingsCard() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiFetch('/api/admin/promo');
    setLoading(false);
    if (error) { toast.error(error); return; }
    const list = data?.promos || [];
    setPromos(list);
    if (!activeKey && list.length > 0) setActiveKey(list[0].key);
  }, [activeKey]);

  useEffect(() => { loadList(); }, [loadList]);

  const doDelete = async () => {
    const k = confirmDelete;
    setConfirmDelete(null);
    const { error } = await apiFetch(`/api/admin/promo/${k}`, { method: 'DELETE' });
    if (error) { toast.error(error); return; }
    toast.success(`Deleted '${k}'`);
    if (activeKey === k) setActiveKey(null);
    loadList();
  };

  return (
    <Card data-testid="promo-settings-card">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-[#00A896] dark:text-[#00E5CC]" />
              Promo Settings
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Edit headline, copy, dates, CTA, and images for site-wide promo banners. Auto-hides after the End date — no code change needed.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowNew(true)}
            data-testid="new-promo-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New promo
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading promos…</p>
        ) : promos.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No promos yet. Click &quot;New promo&quot; to create one.</p>
        ) : (
          <>
            {/* Tab strip — one chip per promo + delete button for custom ones */}
            <div className="flex flex-wrap gap-2 border-b pb-2">
              {promos.map((p) => {
                const isActive = activeKey === p.key;
                const isSeeded = SEEDED_KEYS.includes(p.key);
                return (
                  <div key={p.key} className="flex items-center">
                    <button
                      type="button"
                      onClick={() => setActiveKey(p.key)}
                      className={`px-3 py-1.5 rounded-l-md text-xs font-medium transition-colors ${
                        isActive
                          ? 'bg-[#00A896] text-white dark:bg-[#00E5CC] dark:text-[#0d1f2d]'
                          : 'bg-muted text-foreground hover:bg-muted/70'
                      } ${isSeeded ? 'rounded-r-md' : ''}`}
                      data-testid={`promo-tab-${p.key}`}
                    >
                      {p.headline || p.key}
                      <span className={`ml-2 text-[10px] ${isActive ? 'opacity-80' : 'text-muted-foreground'}`}>
                        {p.key}
                      </span>
                    </button>
                    {!isSeeded && (
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(p.key)}
                        className="px-2 py-1.5 rounded-r-md bg-muted hover:bg-red-500/20 text-muted-foreground hover:text-red-600 transition-colors"
                        title={`Delete promo '${p.key}'`}
                        data-testid={`promo-delete-${p.key}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {activeKey && <PromoEditor promoKey={activeKey} onChange={loadList} />}
          </>
        )}
      </CardContent>

      <NewPromoDialog
        open={showNew}
        onOpenChange={setShowNew}
        onCreated={(k) => { setActiveKey(k); loadList(); }}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete promo?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete <strong>{confirmDelete}</strong>? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="promo-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default PromoSettingsCard;
